import React, {useState, useMemo, useCallback, useRef} from 'react';
import {Box, Paper} from '@mui/material';
import PdfViewer from '../PdfViewer/PdfViewer';
import {Button} from '@mui/material';
import {saveAs} from 'file-saver';
import * as XLSX from 'xlsx-js-style'
import {useTheme} from '@mui/material/styles';
import {useTranslation} from "react-i18next";
import Header from '../Header/Header';
import LanguageThemeBar from '../../LanguageThemeBar';
import {AppBar, Toolbar} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import {useEffect} from 'react'; // Make sure this is imported
import {API_BASE} from '../../../apiBase';
import {apiFetch} from '../../../api/api';

// ViewerContainer.js
export const buildExtractionTableDataWithGroups = (annotations, groupMeta = {}) => {
    const header0 = [];
    const header1 = [];
    const header2 = [];

    const valueWriters = [];
    const columnMeta = [];
    let colIndex = 0;
    let maxDataRows = 1;

    let currentGroupId = null;
    let isFirstInGroup = false;

    annotations.forEach((a) => {
        const groupId = a.groupId || null;
        const groupLabel = groupId ? (groupMeta[groupId]?.name || groupId) : null;

        if (groupId !== currentGroupId) {
            currentGroupId = groupId;
            isFirstInGroup = true;
        }

        const pushScalar = (label, text) => {
            if (groupLabel) {
                // Repeat the group label for every grouped column (Excel parity)
                header0.push(groupLabel);
                header1.push(label);
                header2.push('');
            } else {
                header0.push(label);
                header1.push('');
                header2.push('');
            }
            const myCol = colIndex;
            valueWriters.push({
                width: 1,
                mergeVertical: true,              // <-- mirror Excel’s intent
                fillRow: (r, row) => {
                    if (r === 0) row[myCol] = text ?? '';
                },
            });
            columnMeta.push({mergeVertical: true});
            colIndex += 1;
            isFirstInGroup = false;
        };

        if (a.type === 'summary_row' || a.type === 'entity' || a.type === 'key_value') {
            const label = a.label ?? a.entity_type ?? a.field?.text ?? 'Field';
            const text = a.text ?? a.value?.text ?? '';
            pushScalar(label, text);
            return;
        }

        if (a.type === 'table' || a.type === 'table_row') {
            const isLegacy = a.type === 'table_row';
            const blockTitle = a.label ?? 'Table';
            const rows = Array.isArray(a.rows)
                ? a.rows
                : (isLegacy ? [(a.columns || []).map(c => c?.text ?? '')] : []);

            const firstRow = rows[0] || [];
            const width = Math.max(
                (Array.isArray(a.table_header) ? a.table_header.length : 0),
                firstRow.length
            );
            if (rows.length > 0) maxDataRows = Math.max(maxDataRows, rows.length);

            for (let c = 0; c < width; c++) {
                const headerName = a.table_header?.[c] ?? `Column ${c + 1}`;
                if (groupLabel) {
                    header0.push(groupLabel);             // repeat on every column in the group
                    header1.push(blockTitle);             // repeat title across the table width too
                    header2.push(headerName);
                } else {
                    header0.push(c === 0 ? blockTitle : '');
                    header1.push(headerName);
                    header2.push('');
                }
                columnMeta.push({mergeVertical: false});
            }

            const start = colIndex;
            valueWriters.push({
                width,
                fillRow: (r, row) => {
                    const vals = rows[r] || [];
                    for (let c = 0; c < width; c++) {
                        row[start + c] = vals[c] ?? '';
                    }
                },
            });
            colIndex += width;
            isFirstInGroup = false;
        }
    });

    // Build all data rows
    const rows = [];
    for (let r = 0; r < maxDataRows; r++) {
        const row = new Array(colIndex).fill('');
        valueWriters.forEach(w => w.fillRow(r, row));
        rows.push(row);
    }

    const headers = [header0, header1, header2].filter(r => r.some(v => v));
    // Keep `row` for backward compatibility with any older callers
    return {headers, rows, row: rows[0] || [], columnMeta};
};


// HighlightOverlay.js
const getAnnotationKey = (a) => {
    if (a.__uid) return a.__uid;
    const base = `${a.page}_${a.type}`;

    if (a.type === 'key_value') {
        const v = a.value?.bbox?.join('_') || 'no_val_bbox';
        return `${base}_${v}`;
    }

    if (a.type === 'entity' || a.type === 'summary_row') return `${base}_${a.bbox?.join('_')}`;
    if (a.type === 'table_row' && Array.isArray(a.columns)) {
        const coords = a.columns.map(col => col.bbox?.join('_')).join('|');
        return `${base}_${coords}`;
    }
    if (a.type === 'table' && Array.isArray(a.bbox)) return `${base}_${a.bbox.join('_')}`;
    return `${base}_fallback_${Math.random().toString(36).slice(2, 8)}`;
};


export const getOrderedAnnotations = (annotations, annotationOrder) => {
    const keyMap = Object.fromEntries(annotations.map(a => [getAnnotationKey(a), a]));

    return annotationOrder.flatMap(id => {
        if (id.startsWith('group_')) {
            const groupId = id.replace('group_', '');
            return annotations.filter(a => a.groupId === groupId);
        }
        return keyMap[id];
    }).filter(Boolean);
};


/**
 * ViewerContainer is the main component that wraps the PDF viewer and
 * developer settings panel. It manages state related to PDF processing settings.
 *
 * Props:
 * - file: The PDF file to be rendered in the viewer.
 */
function ViewerContainer({
                             showHeader = true,
                             compactHeader = false,
                             disableExtractionMode = 'latched', // 'latched' (old behavior) | 'live' (follow selection)
                             preventAutoExtract = false,
                             selectedAnnotations,
                             allAnnotations,
                             onAllAnnotationsChange,
                             setSelectedAnnotations,
                             groupMeta: propsGroupMeta,
                             setGroupMeta: propsSetGroupMeta,
                             defaultGroupIdForNewAnns,
                             file_input,
                             hideSelectionPanel = false,
                             readOnlyHighlights = false,
                             forceAllSelected = false,
                             hideToolbar = false,
                             showZoomControls = true,
                             showAnnotateControls = true,
                             peekKeyExternal,
                             peekNonceExternal,
                             hoveredKeyExternal,
                             onBoxHover,
                             // 🔧 optional redraw controls
                             removeKeyExternal,
                             removeNonceExternal,
                             onAnnotationAdded,
                             onAnnotationMoved,
                             focusPageExternal,
                             hideEmptyStateCta = false,
                             containerWidth,
                             containerHeight,
                             containerSx,
                             paperSx,
                             onFileSelected,
                             persistSelection = false,
                             tableWarningText,
                             useOcrExternal,
                             setUseOcrExternal,
                             useDeepScanExternal,
                             setUseDeepScanExternal,
                             spatialCloseness,
                             setSpatialCloseness,
                             disableReprocessOnDrag = false,
                             useSpatialLocality,
                             setUseSpatialLocality,
                             providerExternal,
                             setProviderExternal,
                             modelExternal,
                             setModelExternal,
                             availableModelsExternal,
                             configuredProvidersExternal,
                         }) {
    const {t} = useTranslation();

    const theme = useTheme();

    const navigate = useNavigate();

    const [localProvider, setLocalProvider] = useState('openai');
    const [localModel, setLocalModel] = useState('gpt-4.1');

    // State for available models from user's configured providers
    const [localAvailableModels, setLocalAvailableModels] = useState({});
    const [localConfiguredProviders, setLocalConfiguredProviders] = useState([]);

    // Use external provider/model if provided, otherwise use local state
    const provider = providerExternal !== undefined ? providerExternal : localProvider;
    const model = modelExternal !== undefined ? modelExternal : localModel;

    // Wrapper functions to save preferences when using local state (playground mode)
    const setProvider = useCallback(async (newProvider) => {
        if (setProviderExternal !== undefined) {
            // External control - just call the external setter
            setProviderExternal(newProvider);
        } else {
            // Local control (playground mode)
            setLocalProvider(newProvider);
            // Clear the model when provider changes - user must explicitly select a model
            setLocalModel('');
        }
    }, [setProviderExternal]);

    const setModel = useCallback(async (newModel) => {
        if (setModelExternal !== undefined) {
            // External control - just call the external setter
            setModelExternal(newModel);
        } else {
            // Local control (playground mode) - save to backend
            setLocalModel(newModel);
            // Only save preferences if both provider and model are selected
            if (localProvider && newModel) {
                try {
                    await apiFetch(`${API_BASE}/user/preferences/last-used`, {
                        method: 'POST',
                        headers: {'Content-Type': 'application/json'},
                        body: JSON.stringify({
                            provider: localProvider,
                            model: newModel
                        })
                    });
                } catch (err) {
                    console.error('Failed to save last used provider/model:', err);
                }
            }
        }
    }, [setModelExternal, localProvider]);

    const availableModels = availableModelsExternal !== undefined ? availableModelsExternal : localAvailableModels;
    const configuredProviders = configuredProvidersExternal !== undefined ? configuredProvidersExternal : localConfiguredProviders;

    // Fetch user profile to get configured providers and available models (only if not provided externally)
    useEffect(() => {
        if (availableModelsExternal !== undefined && configuredProvidersExternal !== undefined) {
            return; // Skip fetching if external props are provided
        }

        const fetchUserProfile = async () => {
            try {
                const res = await apiFetch(`${API_BASE}/profile`);
                if (!res.ok) throw new Error('Failed to fetch profile');
                const profile = await res.json();

                // Extract configured providers and their allowed models
                const configured = Object.keys(profile.configured_api_keys || {});
                setLocalConfiguredProviders(configured);
                setLocalAvailableModels(profile.allowed || {});

                // Priority 1: Use last used provider/model (shared context)
                const lastProvider = profile.last_used_provider;
                const lastModel = profile.last_used_model;

                if (lastProvider && lastModel &&
                    configured.includes(lastProvider) &&
                    profile.allowed?.[lastProvider]?.includes(lastModel)) {
                    setLocalProvider(lastProvider);
                    setLocalModel(lastModel);
                }
                // Priority 2: Set default provider and model from admin settings
                else {
                    const defaultProviderFromProfile = profile.default_provider;
                    const defaultModelFromProfile = profile.default_model;

                    if (defaultProviderFromProfile && configured.includes(defaultProviderFromProfile)) {
                        setLocalProvider(defaultProviderFromProfile);
                        if (defaultModelFromProfile) {
                            setLocalModel(defaultModelFromProfile);
                        } else if (profile.allowed?.[defaultProviderFromProfile]?.[0]) {
                            setLocalModel(profile.allowed[defaultProviderFromProfile][0]);
                        }
                    } else if (configured.length > 0) {
                        // Fallback to first available provider
                        const firstProvider = configured[0];
                        setLocalProvider(firstProvider);
                        setLocalModel(profile.allowed?.[firstProvider]?.[0] || '');
                    }
                }
            } catch (err) {
                console.error('Failed to fetch user profile:', err);
            }
        };

        fetchUserProfile();
    }, [availableModelsExternal, configuredProvidersExternal]);

    // Number of lines to process in a single batch
    const [batchSize, setBatchSize] = useState(5);

    // Maximum number of parallel workers for processing
    const [maxWorkers, setMaxWorkers] = useState(4);

    // OCR toggle: false = digital document (pdfplumber with auto-fallback), true = scanned document (force OCR)
    // Use external state if provided (for tool creation), otherwise use local state
    const [localUseOcr, setLocalUseOcr] = useState(false);
    const useOcr = useOcrExternal !== undefined ? useOcrExternal : localUseOcr;
    const setUseOcr = setUseOcrExternal !== undefined ? setUseOcrExternal : setLocalUseOcr;

    // Deep Scan toggle: enhanced OCR for degraded/handwritten documents
    const [localUseDeepScan, setLocalUseDeepScan] = useState(false);
    const useDeepScan = useDeepScanExternal !== undefined ? useDeepScanExternal : localUseDeepScan;
    const setUseDeepScan = setUseDeepScanExternal !== undefined ? setUseDeepScanExternal : setLocalUseDeepScan;

    // Key to trigger re-render of PdfViewer when settings are updated
    const [refreshKey, setRefreshKey] = useState(0);

    // Annotation state - must be declared before handleOcrToggle
    const [annotationsForExport, setAnnotationsForExport] = useState([]);
    const [annotationOrder, setAnnotationOrder] = useState([]);
    const [groupAnnotationOrder, setGroupAnnotationOrder] = useState({});

    // Cache annotation state for each mode to allow instant switching
    const annotationCacheRef = useRef({
        digital: { order: [], groupOrder: {}, forExport: [] },
        scanned: { order: [], groupOrder: {}, forExport: [] }
    });

    // Handler to switch OCR mode and cache/restore annotation state
    const handleOcrToggle = useCallback((newUseOcr) => {
        // Save current state to cache before switching
        const currentMode = useOcr ? 'scanned' : 'digital';
        annotationCacheRef.current[currentMode] = {
            order: annotationOrder,
            groupOrder: groupAnnotationOrder,
            forExport: annotationsForExport
        };

        // Switch mode
        setUseOcr(newUseOcr);

        // Restore cached state for new mode
        const newMode = newUseOcr ? 'scanned' : 'digital';
        const cached = annotationCacheRef.current[newMode];
        setAnnotationOrder(cached.order);
        setGroupAnnotationOrder(cached.groupOrder);
        setAnnotationsForExport(cached.forExport);

        if (typeof onAllAnnotationsChange === 'function') {
            onAllAnnotationsChange(cached.forExport);
        }
    }, [useOcr, setUseOcr, annotationOrder, groupAnnotationOrder, annotationsForExport, onAllAnnotationsChange]);

    const [localGroupMeta, setLocalGroupMeta] = useState({});

    const groupMeta = propsGroupMeta ?? localGroupMeta;
    const setGroupMeta = propsSetGroupMeta ?? setLocalGroupMeta;

    // Initialize state directly from the prop to prevent a null state on the first render.
    const [file, setFile] = useState(file_input || null);

    // Local fallback when parent doesn't manage selection
    const [localSelected, setLocalSelected] = useState([]);
    const effectiveSelected = selectedAnnotations ?? localSelected;
    const effectiveSetSelected = setSelectedAnnotations ?? setLocalSelected;

    const shouldDisableExtraction = useMemo(() => {
        return effectiveSelected && effectiveSelected.length > 0;
    }, [effectiveSelected]);


    // Wrap the setter: ignore "clear" calls from PdfViewer during load
    const setSelectedAnnotationsSafe = useCallback((nextValue) => {
        if (readOnlyHighlights) return;

        // The previous guard against race conditions is no longer needed after fixing the
        // root cause of the infinite loop in PdfViewer.js. A direct update is now safe.
        const val = typeof nextValue === 'function' ? nextValue(effectiveSelected) : nextValue;
        effectiveSetSelected(val);
    }, [readOnlyHighlights, effectiveSelected, effectiveSetSelected])

    // Live: disable when there’s any selection; Latched: match existing behavior
    const liveDisable = useMemo(
        () => (effectiveSelected && effectiveSelected.length > 0),
        [effectiveSelected]
    );
    const [latchedDisable, setLatchedDisable] = useState(false);
    useEffect(() => {
        if (disableExtractionMode === 'latched' && selectedAnnotations?.length > 0) {
            setLatchedDisable(true); // keep legacy one-way latch
        }
    }, [disableExtractionMode, selectedAnnotations]);
    const disableExtraction =
        preventAutoExtract ||
        (disableExtractionMode === 'live'
            ? liveDisable
            : disableExtractionMode === 'off'
                ? false
                : latchedDisable);

    const handleAnnotationsUpdate = useCallback((newAnns) => {
        if (typeof onAllAnnotationsChange === 'function') {
            // Defer the update to the next tick to avoid the "set state in render" error.
            // This breaks the rapid re-render cycle.
            setTimeout(() => {
                onAllAnnotationsChange(newAnns);
            }, 0);
        }
    }, [onAllAnnotationsChange]);

    // Keep seed highlights in sync with the active file *and* current selection
    const [initialHighlights, setInitialHighlights] = useState([]);
    useEffect(() => {
        if (preventAutoExtract && Array.isArray(allAnnotations) && allAnnotations.length > 0) {
            setInitialHighlights(allAnnotations);
        } else if (disableExtraction) {
            setInitialHighlights(Array.isArray(effectiveSelected) ? effectiveSelected.filter(Boolean) : []);
        } else {
            // in playground/extraction mode the backend drives highlights
            setInitialHighlights([]);
        }
    }, [file, effectiveSelected, disableExtraction, preventAutoExtract, allAnnotations]);

    const handleNewFileUpload = useCallback((newFile) => {
        setFile(newFile);
        setAnnotationOrder([]);
        setGroupAnnotationOrder({});
        setGroupMeta({});
        setAnnotationsForExport([]);
        if (typeof onAllAnnotationsChange === 'function') {
            onAllAnnotationsChange([]);
        }
        setRefreshKey(prev => prev + 1);
        if (typeof onFileSelected === 'function') {
            onFileSelected(newFile);
        }
    }, [onAllAnnotationsChange, onFileSelected]);

    // This effect synchronizes the externally-controlled `file_input` prop with the internal state.
    // It's designed to react *only* when the prop changes, allowing other parts of the component
    // (like the Header's file selector) to manage the internal state independently.
    useEffect(() => {
        if (file_input !== file) {
            handleNewFileUpload(file_input || null);
        }
    }, [file_input, handleNewFileUpload]);

    // Removed debug logging - state updates tracked via React DevTools
    useEffect(() => {
        // Effect for tracking state changes
    }, [file, preventAutoExtract, disableExtraction, effectiveSelected, initialHighlights]);

    function getOrderedAnnotations(annotations, annotationOrder, groupAnnotationOrder) {
// HighlightOverlay.js
        const getAnnotationKey = (a) => {
            if (a.__uid) return a.__uid;
            const base = `${a.page}_${a.type}`;

            if (a.type === 'key_value') {
                const v = a.value?.bbox?.join('_') || 'no_val_bbox';
                return `${base}_${v}`;
            }

            if (a.type === 'entity' || a.type === 'summary_row') return `${base}_${a.bbox?.join('_')}`;
            if (a.type === 'table_row' && Array.isArray(a.columns)) {
                const coords = a.columns.map(col => col.bbox?.join('_')).join('|');
                return `${base}_${coords}`;
            }
            if (a.type === 'table' && Array.isArray(a.bbox)) return `${base}_${a.bbox.join('_')}`;
            return `${base}_fallback_${Math.random().toString(36).slice(2, 8)}`;
        };


        const keyMap = new Map(annotations.map(a => [getAnnotationKey(a), a]));

        const ordered = [];

        for (const id of annotationOrder) {
            if (id.startsWith('group_')) {
                const groupId = id.replace('group_', '');
                const children = groupAnnotationOrder[groupId] || [];
                for (const childId of children) {
                    const annotation = keyMap.get(childId);
                    if (annotation) ordered.push(annotation);
                }
            } else {
                const annotation = keyMap.get(id);
                if (annotation && !annotation.groupId) {
                    ordered.push(annotation);
                }
            }
        }

        return ordered;
    }


    const exportToCsv = (annotations) => {
        const header1 = [];
        const header2 = [];
        const row = [];

        annotations.forEach((a, idx) => {
            const sectionTitle = `Block ${idx + 1}`;

            if (a.type === 'key_value') {
                header1.push(sectionTitle, sectionTitle);
                header2.push('Field', 'Value');
                row.push(a.field?.text ?? '', a.value?.text ?? '');
            } else if (a.type === 'entity') {
                header1.push(sectionTitle, sectionTitle);
                header2.push('Entity Type', 'Text');
                row.push(a.entity_type ?? '', a.text ?? '');
            } else if (a.type === 'summary_row') {
                header1.push(sectionTitle, sectionTitle);
                header2.push('Label', 'Text');
                row.push(a.label ?? '', a.text ?? '');
            } else if (a.type === 'table') {
                const firstRow = Array.isArray(a.rows) && a.rows.length ? a.rows[0] : [];
                firstRow.forEach((cell, i) => {
                    const h = a.table_header?.[i] ?? `Col ${i + 1}`;
                    header1.push(sectionTitle);
                    header2.push(h);
                    row.push(cell ?? '');
                });
            }
        });

        const csvContent =
            [header1.join(','), header2.join(','), row.map(v => `"${v}"`).join(',')].join('\n');

        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
        saveAs(blob, 'extracted_data.csv');
    };

    const exportToExcel = (annotations, groupMeta = {}) => {
        // De-dup by annotation key
        const seenKeys = new Set();
        annotations = annotations.filter(a => {
            const key = getAnnotationKey(a);
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
        });

        const wb = XLSX.utils.book_new();
        const ws_data = [];

        // 3 header rows (group / block label / column header)
        const header0 = [];
        const header1 = [];
        const header2 = [];

        const merges = [];
        let colIndex = 0;

        // For borders
        let lastGroup = null;
        let currentGroupStart = null;
        const groupBoundaries = [];

        // We'll build the data rows after we know the maximum row count
        const valueWriters = [];
        let maxDataRows = 1;

        annotations.forEach((a) => {
            const groupLabel = a.groupId ? (groupMeta[a.groupId]?.name || a.groupId) : null;
            const startCol = colIndex;

            if (a.type === 'summary_row' || a.type === 'entity' || a.type === 'key_value') {
                const label = a.label ?? a.entity_type ?? a.field?.text ?? 'Field';
                const text = a.text ?? a.value?.text ?? '';

                if (groupLabel) {
                    header0.push(groupLabel);
                    header1.push(label);
                    header2.push('');
                    merges.push({s: {r: 1, c: colIndex}, e: {r: 2, c: colIndex}});
                } else {
                    header0.push(label);
                    header1.push('');
                    header2.push('');
                    merges.push({s: {r: 0, c: colIndex}, e: {r: 2, c: colIndex}});
                }

                const myCol = colIndex;
                // Write only on first data row; merge vertically across all data rows later
                valueWriters.push({
                    startCol: myCol,
                    width: 1,
                    mergeVertical: true,
                    fillRow: (r, row) => {
                        if (r === 0) row[myCol] = text ?? '';
                    }
                });
                colIndex += 1;
            } else if (a.type === 'table' || a.type === 'table_row') {
                // New format preferred: { type:"table", rows: string[][], table_header:string[] }
                // Legacy fallback (single-row): { type:"table_row", columns:[{text}], table_header:string[] }
                const isLegacy = a.type === 'table_row';
                const blockTitle = a.label ?? 'Table';
                const rows = Array.isArray(a.rows)
                    ? a.rows
                    : (isLegacy ? [(a.columns || []).map(c => c?.text ?? '')] : []);

                const firstRow = rows[0] || [];
                const width = Math.max(
                    (Array.isArray(a.table_header) ? a.table_header.length : 0),
                    firstRow.length
                );
                if (rows.length > 0) maxDataRows = Math.max(maxDataRows, rows.length);

                for (let index = 0; index < width; index++) {
                    if (groupLabel) {
                        header0.push(groupLabel);
                        header1.push(blockTitle);
                        const headerName = a.table_header?.[index] ?? `Column ${index + 1}`;
                        header2.push(headerName);
                    } else {
                        header0.push(index === 0 ? blockTitle : '');
                        const headerName = a.table_header?.[index] ?? `Column ${index + 1}`;
                        header1.push(headerName);
                        header2.push('');
                        merges.push({s: {r: 1, c: colIndex + index}, e: {r: 2, c: colIndex + index}});
                    }
                }
                // Merge the middle header across table columns if grouped; otherwise merge the top header
                if (groupLabel) merges.push({s: {r: 1, c: colIndex}, e: {r: 1, c: colIndex + width - 1}});
                else merges.push({s: {r: 0, c: colIndex}, e: {r: 0, c: colIndex + width - 1}});

                const myStart = colIndex;
                valueWriters.push({
                    startCol: myStart,
                    width,
                    fillRow: (r, row) => {
                        const vals = rows[r] || [];
                        for (let c = 0; c < width; c++) {
                            row[myStart + c] = vals[c] ?? '';
                        }
                    }
                });
                colIndex += width;
            }

            // Track group boundaries for borders
            const endCol = colIndex - 1;
            if (groupLabel !== lastGroup) {
                if (lastGroup !== null && currentGroupStart !== null) {
                    groupBoundaries.push({start: currentGroupStart, end: startCol - 1});
                }
                currentGroupStart = startCol;
                lastGroup = groupLabel;
            }
            if (!groupLabel) {
                groupBoundaries.push({start: startCol, end: endCol});
                currentGroupStart = null;
                lastGroup = null;
            }
        });
        // Close last group if needed
        if (lastGroup !== null && currentGroupStart !== null) {
            groupBoundaries.push({start: currentGroupStart, end: colIndex - 1});
        }
        // Build sheet rows
        ws_data.push(header0);
        ws_data.push(header1);
        ws_data.push(header2);
        for (let r = 0; r < maxDataRows; r++) {
            const row = new Array(colIndex).fill('');
            valueWriters.forEach(w => w.fillRow(r, row));
            ws_data.push(row);
        }

        // Vertically merge non-table columns across the data region (if more than one data row)
        if (maxDataRows > 1) {
            valueWriters.forEach(w => {
                if (w.mergeVertical && w.width === 1) {
                    merges.push({
                        s: {r: 3, c: w.startCol},
                        e: {r: 3 + maxDataRows - 1, c: w.startCol}
                    });
                }
            });
        }

        // Merge contiguous identical group labels in header row 0
        const groupSpans = {};
        header0.forEach((label, index) => {
            if (!label) return;
            if (!groupSpans[label]) groupSpans[label] = [index, index];
            else groupSpans[label][1] = index;
        });
        for (const [_, [start, end]] of Object.entries(groupSpans)) {
            if (start < end) merges.push({s: {r: 0, c: start}, e: {r: 0, c: end}});
        }

        // Create sheet and style
        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        ws['!merges'] = merges;

        // Column widths
        ws['!cols'] = ws_data[0].map((_, colIdx) => {
            const colTexts = ws_data.map(row => row[colIdx] ?? '');
            const maxLen = Math.max(...colTexts.map(val => val.toString().length));
            return {wch: Math.min(Math.max(10, maxLen + 2), 60)};
        });

        // Header style
        const centerBoldHeader = {
            alignment: {horizontal: 'center', vertical: 'center'},
            font: {bold: true},
            border: {
                top: {style: 'thin', color: {rgb: '000000'}},
                bottom: {style: 'thin', color: {rgb: '000000'}},
                left: {style: 'thin', color: {rgb: '000000'}},
                right: {style: 'thin', color: {rgb: '000000'}},
            },
            fill: {fgColor: {rgb: "E0E0E0"}}
        };
        for (let c = 0; c < colIndex; c++) {
            [0, 1, 2].forEach(r => {
                const cell = XLSX.utils.encode_cell({r, c});
                if (ws[cell]) ws[cell].s = centerBoldHeader;
            });
        }

        // Apply borders to *all* data rows
        for (let r = 3; r < ws_data.length; r++) {
            for (let c = 0; c < colIndex; c++) {
                const cell = XLSX.utils.encode_cell({r, c});
                const border = {
                    top: {style: 'hair', color: {rgb: '999999'}},
                    bottom: {style: 'hair', color: {rgb: '999999'}},
                    left: {style: 'none'},
                    right: {style: 'none'}
                };
                const isStart = groupBoundaries.some(range => c === range.start);
                const isEnd = groupBoundaries.some(range => c === range.end);
                if (c === 0 || isStart) border.left = {style: 'thin', color: {rgb: '000000'}};
                if (isEnd || c === colIndex - 1) border.right = {style: 'thin', color: {rgb: '000000'}};
                const style = {alignment: {horizontal: 'left', vertical: 'center'}, border};
                if (ws[cell]) ws[cell].s = style;
            }
        }

        XLSX.utils.book_append_sheet(wb, ws, 'Extractions');
        XLSX.writeFile(wb, 'extracted_data.xlsx');
    };

    return (
        <>
            {showHeader && (
                <AppBar position="static" color="transparent" elevation={0}>
                    <Toolbar
                        variant={compactHeader ? 'dense' : 'regular'}
                        sx={{
                            justifyContent: 'space-between',
                            px: 2,
                            minHeight: compactHeader ? 44 : undefined,
                            py: compactHeader ? 0 : 1,
                        }}
                    >
                        <Header
                            onFileSelect={handleNewFileUpload}
                            compact={compactHeader}
                            useOcr={useOcr}
                            setUseOcr={handleOcrToggle}
                            useDeepScan={useDeepScan}
                            setUseDeepScan={setUseDeepScan}
                            spatialCloseness={spatialCloseness}
                            setSpatialCloseness={setSpatialCloseness}
                            useSpatialLocality={useSpatialLocality}
                            setUseSpatialLocality={setUseSpatialLocality}
                            provider={provider}
                            setProvider={setProvider}
                            model={model}
                            setModel={setModel}
                            availableModels={availableModels}
                            configuredProviders={configuredProviders}
                        />
                        {!compactHeader && (
                            <Box display="flex" alignItems="center">
                                <LanguageThemeBar/>
                            </Box>
                        )}
                    </Toolbar>
                </AppBar>
            )}
            <Box
                position="relative"
                flexGrow={1}
                overflow="hidden"
                display="flex"
                justifyContent="center"
                alignItems="center"
                width={containerWidth ?? (showHeader ? "98%" : "100%")}
                height={
                    containerHeight ?? (
                        showHeader
                            ? (compactHeader ? 'calc(100vh - 210px)' : 'calc(100vh - 125px)')
                            : 'calc(100vh - 130px)'
                    )
                } bgcolor={theme.palette.background.default}
                sx={{
                    mx: showHeader ? 2 : 0,
                    ...containerSx,
                }}
            >
                {/*            <Button
                variant="contained"
                sx={{ position: 'absolute', top: 20, right: 30, zIndex: 1500 }}
                onClick={() => exportToCsv(annotationsForExport)}
            >
                Export to CSV
            </Button>*/}

                {showHeader && !compactHeader && (
                    <Button
                        variant="outlined"
                        sx={{
                            position: 'fixed',
                            top: 20,
                            left: 20,
                            zIndex: 2500,
                        }}
                        onClick={() => navigate('/home')}
                    >
                        {t("homepage")}
                    </Button>
                )}

                {showHeader && !compactHeader && (
                    <Button
                        variant="contained"
                        disabled={!annotationsForExport || annotationsForExport.length === 0}
                        sx={{position: 'absolute', top: 10, right: 20, zIndex: 1500}}
                        onClick={() => {
                            exportToExcel(annotationsForExport, groupMeta);
                        }}
                    >
                        {t("export_to_excel")}
                    </Button>
                )}

                {/* Main viewer container */}
                <Paper
                    elevation={6}
                    sx={{
                        width: '100%',
                        maxWidth: '2400px',
                        height: '100%',
                        overflow: 'auto',
                        p: 0, // remove padding
                        border: '2px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.background.default,
                        marginLeft: 0,
                        marginRight: 0,
                    }}
                >
                    <PdfViewer
                        key={refreshKey}
                        defaultGroupIdForNewAnns={defaultGroupIdForNewAnns}
                        refreshKey={refreshKey}
                        file={file}
                        provider={provider}
                        model={model}
                        batchSize={batchSize}
                        maxWorkers={maxWorkers}
                        useOcr={useOcr}
                        useDeepScan={useDeepScan}
                        onExportData={setAnnotationsForExport}
                        annotationOrder={annotationOrder}
                        setAnnotationOrder={setAnnotationOrder}
                        groupAnnotationOrder={groupAnnotationOrder}
                        setGroupAnnotationOrder={setGroupAnnotationOrder}
                        groupMeta={groupMeta}
                        setGroupMeta={setGroupMeta}
                        selectedAnnotations={effectiveSelected}
                        setSelectedAnnotations={setSelectedAnnotationsSafe}
                        initialHighlights={initialHighlights}
                        disableExtraction={disableExtraction}
                        onAnnotationsChange={handleAnnotationsUpdate}
                        hideSelectionPanel={hideSelectionPanel}
                        disableInteractions={readOnlyHighlights}
                        forceAllSelected={forceAllSelected}
                        hideToolbar={hideToolbar}
                        showZoomControls={showZoomControls}
                        showAnnotateControls={showAnnotateControls}
                        hideEmptyStateCta={hideEmptyStateCta}
                        externalPeekKey={peekKeyExternal}
                        externalPeekNonce={peekNonceExternal}
                        externalHoveredKey={hoveredKeyExternal}
                        onBoxHover={onBoxHover}
                        externalRemoveKey={removeKeyExternal}
                        externalRemoveNonce={removeNonceExternal}
                        onAnnotationAdded={onAnnotationAdded}
                        onAnnotationMoved={onAnnotationMoved}
                        externalFocusPage={focusPageExternal}
                        tableWarningText={tableWarningText}
                        persistSelection={persistSelection}
                        disableReprocessOnDrag={disableReprocessOnDrag}
                    />
                </Paper>
            </Box>
        </>

    );
}


export default ViewerContainer;
